/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, type PropsWithChildren, type ReactNode, useEffect, useMemo, useRef } from 'react';

// React Router
import {
    type DataStrategyResult,
    isRouteErrorResponse,
    Links,
    Meta,
    type MetaDescriptor,
    type MiddlewareFunction,
    Navigate,
    Outlet,
    Scripts,
    ScrollRestoration,
    useRevalidator,
    useRouteLoaderData,
} from 'react-router';
import type { Route } from './+types/root';
import { routes } from '@/route-paths';

// Third-party libraries
import { createInstance, type i18n } from 'i18next';
import { I18nextProvider, useTranslation, initReactI18next } from 'react-i18next';
import { PageDesignerProvider } from '@salesforce/storefront-next-runtime/design/react/core';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { dataStoreMiddleware, getGcpApiKey } from '@salesforce/storefront-next-runtime/data-store';
import {
    buildUrl,
    SiteProvider,
    siteContext,
    type Site,
    type Locale,
} from '@salesforce/storefront-next-runtime/site-context';

// Middlewares
import authMiddlewareServer, { getAuth as getAuthServer } from '@/middlewares/auth.server';
import { getPublicSessionData } from '@/middlewares/auth.utils';
import createBasketMiddleware, { basketResourceContext, type BasketSnapshot } from '@/middlewares/basket.server';
import shopperContextMiddlewareServer from '@/middlewares/shopper-context.server';
import { shopperConfigMiddlewareServer } from '@/middlewares/shopper-config.server';
import storeResolutionMiddlewareServer, { getStoreResolution } from '@/middlewares/store-resolution.server';
import legacyRoutesMiddlewareClient from '@/middlewares/legacy-routes.client';
import {
    performanceMetricsMiddlewareClient,
    performanceMetricsMiddlewareServer,
} from '@/middlewares/performance-metrics';
import { appConfigMiddlewareServer } from '@/middlewares/app-config.server';
import { appConfigMiddlewareClient } from '@/middlewares/app-config.client';
import { ConfigProvider, getConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { siteContextMiddleware } from '@/middlewares/site-context.server';
import { i18nextMiddleware } from '@/middlewares/i18next.server';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import {
    selectedStoreMiddleware,
    selectedStoreContext,
} from '@/extensions/store-locator/middlewares/selected-store.server';
import type { SelectedStoreInfo } from '@/extensions/store-locator/stores/store-locator-store';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { correlationMiddleware } from '@/middlewares/correlation.server';
import { cacheControlMiddleware } from '@/middlewares/cache-control.server';
import { requestOriginMiddleware } from '@/middlewares/request-origin';
import { getAppOrigin } from '@/lib/origin';
import { loggingMiddleware } from '@/middlewares/logging.server';
import { pageDesignerResolutionMiddleware } from '@/middlewares/page-designer-page-resolution.server';
import { siteUrlConfigMiddleware } from '@/middlewares/site-url-config.server';
import { modeDetectionMiddlewareServer, modeDetectionMiddlewareClient } from '@/middlewares/mode-detection';
import { maintenanceMiddleware } from '@/middlewares/maintenance.server';
import { getSecurityNonce } from '@salesforce/storefront-next-runtime/security';
import { useSecurityNonceFromContext } from '@salesforce/storefront-next-runtime/security/react';

// Providers
import AuthProvider from '@/providers/auth';
import ScopeUpgradeLoginPrompt from '@/components/login/scope-upgrade-login-prompt';
import BasketProvider from '@/providers/basket';
import { ComposeProviders } from '@/providers/compose-providers';
import { CorrelationProvider } from '@/providers/correlation';
import { correlationContext } from '@/lib/correlation';
import MarketingActivityProvider from '@/providers/marketing-activity';
import SearchSuggestionsProvider from '@/providers/search-suggestions';
import LivePersonProvider from '@/providers/live-person';

// Components
import { AppToaster } from '@/components/toast';
import { TrackingConsentBanner } from '@/components/tracking-consent-banner';
import { OneTrustBridge } from '@/components/onetrust-bridge';
import ShopperAgent from '@/components/shopper-agent';
import CompanyLogo from '@/components/company-logo';
import ErrorPageImage from '@/components/icons/error-page-image';
import { Button } from '@/components/ui/button';
import Footer from '@/components/footer';
import Header from '@/components/header';
import ResponsiveNavigationMenu from '@/components/navigation-menu-responsive';
import type { loader as appLoader } from '@/routes/_app';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import type { ShopperProducts } from '@/scapi';

// Hooks
import { useExecutePendingAction } from '@/hooks/use-execute-pending-action';

// Lib/Utils
import type { PublicSessionData } from '@/lib/api/types';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { initI18next } from '@salesforce/storefront-next-runtime/i18n/client';
import { PageViewTracker } from '@/analytics/page-view-tracker';
import { initializeRegistry } from '@/lib/page-designer/static-registry';
import { buildSeoMetaDescriptors } from '@/utils/seo';

// Assets
import favicon from '/favicon.ico';

// Fonts — ?url imports give Vite/MRT-resolved public paths for <link rel="preload">
import sen from '/fonts/sen-variable.woff2';
import chesnaRegular from '@fonts/ChesnaGrotesk/chesnagrotesk-regular.woff2?url';
import publicoRoman from '@fonts/PublicoHeadline/publicoheadline-roman.woff2?url';
// Side-effect: registers all brand fonts via the FontFace API on first client load
import '@/lib/load-fonts';

// Styles
import { PageDesignerInit } from '@/page-designer-init';
import appStylesHref from './theme/index.css?url';

// Extensions
import { UITargetProviders } from '@/targets/ui-target-providers';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import StoreLocatorProvider from '@/extensions/store-locator/providers/store-locator';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { StoreAndDeliveryZipProvider } from '@/providers/store-and-delivery-zip-provider';
import { ZipUpdateToast } from '@/components/zip-update-toast';
import type { DeliveryZip, ResolvedStore } from '@/lib/store-resolution';
import type { ZipFlashCode } from '@/lib/store-resolution-constants';
import { type Maintenance, maintenanceContext } from '@/lib/maintenance';

export const links: Route.LinksFunction = () => {
    return [
        // Preload critical fonts — required for font-display:optional to work (browser needs
        // the file in cache before the ~100ms window expires, so preload must fire from <head>)
        { rel: 'preload', href: sen, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: chesnaRegular, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: publicoRoman, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: appStylesHref, as: 'style' },
        { rel: 'stylesheet', href: appStylesHref },
    ];
};

export const meta: Route.MetaFunction = ({ loaderData }) => {
    return loaderData?.seoMeta ?? [];
};

export const middleware: MiddlewareFunction<Response>[] = [
    correlationMiddleware,
    // Registered early so it is outermost: on the response phase it therefore runs
    // last and sees the final headers. Gated on `CACHE_CONTROL_NO_STORE_ENABLED`,
    // which `mrt-env.config.json` sets for every MRT environment; while unset
    // (e.g. a local run) this is a pass-through. Scope is blanket (no-store
    // everywhere) so the Fastly /cart fix is measurable, and must be narrowed to
    // stateful routes only before production. See cache-control.server.
    cacheControlMiddleware,
    requestOriginMiddleware,
    loggingMiddleware,
    modeDetectionMiddlewareServer,
    appConfigMiddlewareServer,
    // securityHeadersMiddleware intentionally not registered — matches Ashley `main`, which
    // never enforced a CSP. GA's default CSP blocks scene7 images + Acima/PayPal/Synchrony/
    // Converge iframes + Zeta/Einstein analytics. Re-enabling is the D3 security-hardening follow-up.
    siteContextMiddleware, // Must run after appConfig, before i18next and currency
    ...dataStoreMiddleware,
    siteUrlConfigMiddleware, // Must run after siteContextMiddleware (entry key uses site id)
    i18nextMiddleware,
    pageDesignerResolutionMiddleware,
    selectedStoreMiddleware /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
    performanceMetricsMiddlewareServer,
    maintenanceMiddleware,
    authMiddlewareServer,
    // reconcileFromCustomerWhenCookieless: hybrid AN↔SFN SSO. When the shopper arrives from Ashley NEXT
    // with a basket built on the shared SLAS session but no SFN `__sfdc_basket` cookie, resolve that basket
    // by identity so the header mini-cart / cart-freshness reflect it on the first request (see basket.server).
    createBasketMiddleware({ reconcileFromCustomerWhenCookieless: true }),
    storeResolutionMiddlewareServer,
    shopperContextMiddlewareServer,
    shopperConfigMiddlewareServer,
];

export const clientMiddleware: MiddlewareFunction<Record<string, DataStrategyResult>>[] = [
    // Client middleware functions have varying return types, but React Router expects Record<string, DataStrategyResult>
    // We cast through unknown to avoid type errors while maintaining runtime correctness
    appConfigMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Must run first to set config in context
    modeDetectionMiddlewareClient,
    legacyRoutesMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Checks hybrid.enabled, needs config from context
    performanceMetricsMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>,
];

// On the client side, initialize i18next.
// (On the server side, it's initialized elsewhere in middlewares/i18next.ts file)
// Read the language from the server-rendered HTML to avoid language detection issues
const i18nextOnClient =
    typeof window !== 'undefined'
        ? initI18next({
              language: document.documentElement.lang || undefined,
              // The import() must live here so Vite can resolve the path at build time
              loadLocale: (language) => import(`@/locales/${language}/index.ts`),
          })
        : undefined;

export const loader = ({
    context,
    request,
}: Route.LoaderArgs): {
    // Public auth data - only non-sensitive fields, safe to serialize
    clientAuth: PublicSessionData;
    appConfig: AppConfig;
    basketSnapshot: BasketSnapshot | null;
    maintenance: Maintenance;
    locale: Locale;
    site: Site;
    currency: string;
    selectedStoreInfo: SelectedStoreInfo | null /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
    storeResolution: {
        resolved: ResolvedStore;
        deliveryZip: DeliveryZip;
        flash: ZipFlashCode | null;
    } | null;
    correlationId: string;
    pageDesignerMode: 'EDIT' | 'PREVIEW' | undefined;
    // Pre-computed in the loader (server-only) so seo.ts stays out of the client bundle
    seoMeta: MetaDescriptor[];
    // OOTB GCP Address Autocomplete API key sourced from the MRT data store
    gcpApiKeyFromDAL: string;
    // Return as function to prevent i18next instance serialization
    getI18next: () => i18n;
    // Serialized error namespace for the active locale — used by ErrorBoundary on SSR/hydration
    errorTranslations: Record<string, unknown>;
    // Serialized footer namespace — used by ErrorBoundary to render the real Footer with translations
    footerTranslations: Record<string, unknown>;
    // Serialized header-related namespaces — used by ErrorBoundary to render the real Header with translations
    headerTranslations: Record<string, unknown>;
    accountTranslations: Record<string, unknown>;
    cartTranslations: Record<string, unknown>;
    miniCartTranslations: Record<string, unknown>;
    extStoreLocatorTranslations: Record<string, unknown> /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
    // CSP nonce for inline scripts; null when security headers middleware is disabled
    nonce: string | null;
} => {
    const session = getAuthServer(context);

    const appConfig = getConfig(context);

    // On the server side, our middleware stores the translations in this i18next object
    // so we'll need to be careful not to accidentally serialize this object (to avoid bloating the html).
    const { i18next } = getTranslation(context);

    // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
    const selectedStoreInfo = context.get(selectedStoreContext) ?? null;
    // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR

    // BROWSE-47: store-resolution waterfall result (resolved store + delivery
    // ZIP + one-shot flash code). The middleware always seeds this when it
    // runs; null only if the middleware crashed before context.set().
    const storeResolution = getStoreResolution(context);

    // Get resolved site, locale, and currency from site context middleware
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        throw new Error('Site context not found. Ensure siteContextMiddleware runs before loaders.');
    }
    const { locale, site, currency } = siteCtx;

    // Load the application basket provider with the basket snapshot. We are actively not loading the basket, as
    // we want to lazy load the basket when the basket is needed. This prevents low-engagement users from causing
    // unnecessary resource usage in the form of basket creations.
    const basketSnapshot = context.get(basketResourceContext)?.snapshot ?? null;

    // Get correlation ID from middleware for request tracing
    const correlationId = context.get(correlationContext);

    // Get maintenance data from middleware
    const maintenance = context.get(maintenanceContext);

    // Extract only non-sensitive fields for client - tokens stay server-side only
    const clientAuth = getPublicSessionData(session);

    const requestUrl = new URL(request.url);

    const seoMeta = buildSeoMetaDescriptors({
        site,
        appConfig,
        // Use the resolved public origin (custom domain on MRT) rather than
        // requestUrl.origin (lambda-internal hostname on hybrid deployments)
        // so canonical/hreflang URLs match what the customer is actually browsing.
        origin: getAppOrigin(context),
        locale,
        location: { pathname: requestUrl.pathname, search: requestUrl.search },
    });

    // CSP nonce produced by securityHeadersMiddleware; null when middleware is disabled.
    const nonce = getSecurityNonce(context);

    return {
        appConfig,
        basketSnapshot,
        locale,
        site,
        currency,
        selectedStoreInfo /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
        storeResolution,
        correlationId,
        maintenance,
        clientAuth,
        seoMeta,
        gcpApiKeyFromDAL: getGcpApiKey(context),
        getI18next: () => i18next,
        errorTranslations: (i18next.getResourceBundle(i18next.language, 'routeError') as Record<string, unknown>) ?? {},
        footerTranslations: (i18next.getResourceBundle(i18next.language, 'footer') as Record<string, unknown>) ?? {},
        headerTranslations: (i18next.getResourceBundle(i18next.language, 'header') as Record<string, unknown>) ?? {},
        accountTranslations: (i18next.getResourceBundle(i18next.language, 'account') as Record<string, unknown>) ?? {},
        cartTranslations: (i18next.getResourceBundle(i18next.language, 'cart') as Record<string, unknown>) ?? {},
        miniCartTranslations:
            (i18next.getResourceBundle(i18next.language, 'miniCart') as Record<string, unknown>) ?? {},
        extStoreLocatorTranslations:
            (i18next.getResourceBundle(i18next.language, 'extStoreLocator') as Record<string, unknown>) ??
            {} /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
        pageDesignerMode: isDesignModeActive(request) ? 'EDIT' : isPreviewModeActive(request) ? 'PREVIEW' : undefined,
        nonce,
    };
};

// This creates a union type where properties unique to either loader are optional
// Properties present in both loaders remain required
type ServerLoaderData = ReturnType<typeof loader>;
type LoaderData = ServerLoaderData;

export function Layout({ children }: PropsWithChildren) {
    const data = useRouteLoaderData<LoaderData>('root');
    const appConfig = data?.appConfig;
    const appConfigScript = appConfig
        ? `window.__APP_CONFIG__ = ${JSON.stringify(appConfig)
              .replace(/</g, '\\u003c')
              .replace(/>/g, '\\u003e')
              .replace(/&/g, '\\u0026')
              .replace(/\u2028/g, '\\u2028')
              .replace(/\u2029/g, '\\u2029')};`
        : '';
    // React 19 omits the attribute when the value is undefined, so coerce null/missing to undefined.
    // Falls back to the NonceContext so the error path (root loader threw → no
    // loader data) still gets a valid nonce on its inline scripts. The middleware
    // sets `securityContext` *before* `next()`, and `entry.server.tsx` wraps
    // the SSR tree with <NonceContext.Provider>, so the context is populated
    // even when the loader fails.
    const nonceFromContext = useSecurityNonceFromContext();
    const nonce = data?.nonce ?? nonceFromContext ?? undefined;

    const i18next = typeof window === 'undefined' ? data?.getI18next?.() : i18nextOnClient;
    const lang = i18next?.language ?? 'en';
    const dir = i18next?.dir(lang) ?? 'ltr';

    // INT-43 AC8: inject Constructor.io's hosted behavioural beacon
    // `<script defer>` once per page when the dual flag is on. Placed
    // in `<head>` because `defer` is only honoured by the HTML spec on
    // scripts in `<head>`; the hosted snippet then handles
    // `ConstructorioID_*` cookies, page-view tracking, and exposes
    // `window.cnstrc` for the four header beacon events the
    // search-suggestions Constructor adapter dispatches into. No
    // script is rendered when either flag/url is missing — that is
    // the explicit OFF path the AC8 test asserts.
    const beacon = appConfig?.constructorIo?.beacon;
    const beaconScriptUrl =
        beacon?.enabled && typeof beacon.scriptUrl === 'string' && beacon.scriptUrl.length > 0
            ? beacon.scriptUrl
            : undefined;

    // INT-60 PR 3: OneTrust CMP script injection. Two <script> tags plus an
    // inline OptanonWrapper() callback are rendered into <head> only when
    // both `appConfig.onetrust.enabled === true` AND a non-empty
    // `dataDomainScript` is configured. Missing/blank dataDomainScript is
    // treated as OFF to prevent a broken `<script src=".../consent//..."`
    // request in misconfigured envs. Scripts share the CSP nonce so the D3
    // security-hardening follow-up can flip `defaultSecurityHeaders` on
    // without a separate audit. See docs/README-SECURITY-HEADERS.md and the
    // CSP requirements block in `config.server.ts` app.onetrust for the
    // origin allowlist.
    //
    // `onetrustScriptConfig` is built once so downstream JSX can narrow to a
    // defined value without non-null assertions.
    const onetrustScriptConfig =
        appConfig?.onetrust?.enabled === true &&
        typeof appConfig.onetrust.dataDomainScript === 'string' &&
        appConfig.onetrust.dataDomainScript.length > 0
            ? {
                  dataDomainScript: appConfig.onetrust.dataDomainScript,
                  cdnBase: appConfig.onetrust.cdnBase ?? 'https://cdn.cookielaw.org',
                  language: appConfig.onetrust.language ?? 'en',
                  documentLanguage: appConfig.onetrust.documentLanguage === true,
              }
            : undefined;
    // The inline OptanonWrapper() is what OT calls after every consent
    // decision. It flips a same-origin ready flag and dispatches a plain
    // CustomEvent that `<OneTrustBridge />` subscribes to — no globals
    // beyond the two OneTrust already sets. Kept small and dependency-free
    // so it runs before hydration finishes.
    const optanonWrapperInline = `window.__ashleyOnetrustReady=false;window.OptanonWrapper=function(){window.__ashleyOnetrustReady=true;try{window.dispatchEvent(new CustomEvent('ashley:onetrust:consent-changed'));}catch(e){}};`;

    return (
        <html lang={lang} dir={dir}>
            <head>
                <meta charSet="utf-8" />
                <link rel="icon" type="image/x-icon" href={favicon} />
                {appConfig?.links?.preconnect?.map((origin: string) => (
                    <link key={origin} rel="preconnect" href={origin} />
                ))}
                {appConfig?.links?.prefetchDns?.map((origin: string) => (
                    <link key={origin} rel="dns-prefetch" href={origin} />
                ))}
                {appConfig?.links?.prefetch?.map((href: string) => (
                    <link key={href} rel="prefetch" href={href} />
                ))}
                <script
                    nonce={nonce}
                    // Browsers strip the `nonce` content attribute from the DOM after
                    // applying CSP (so scripts can't read each other's nonce), leaving
                    // `nonce=""` in the client DOM while the server rendered the real
                    // value. On this hand-written inline script that surfaces as a
                    // hydration attribute mismatch. React's own <Scripts>/<ScrollRestoration>
                    // handle this internally; here we suppress the warning for the same
                    // browser behavior. The CSP nonce itself still applies correctly.
                    //
                    // Note: this also suppresses any server/client divergence of the
                    // __APP_CONFIG__ body, not just the nonce attribute. Safe today
                    // because appConfig is server-rendered and never mutated on the
                    // client. If client-side config mutation is ever introduced, revisit
                    // this — a real config mismatch would be silently hidden here.
                    suppressHydrationWarning
                    // Inline SSR config bootstrap — a well-known pattern used by
                    // React Router / Next.js / Remix. The `appConfigScript`
                    // content is produced server-side from `JSON.stringify` with
                    // all HTML-critical characters (<, >, &, U+2028, U+2029)
                    // escaped as \u sequences above, and the <script> tag carries
                    // a CSP nonce. Not user-controlled content — safe.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: `
                        ${appConfigScript}
                    `,
                    }}
                />
                {beaconScriptUrl && <script defer src={beaconScriptUrl} data-testid="constructor-beacon-script" />}
                {onetrustScriptConfig && (
                    <>
                        <script
                            nonce={nonce}
                            src={`${onetrustScriptConfig.cdnBase}/consent/${onetrustScriptConfig.dataDomainScript}/OtAutoBlock.js`}
                            data-testid="onetrust-autoblock-script"
                        />
                        <script
                            nonce={nonce}
                            src={`${onetrustScriptConfig.cdnBase}/scripttemplates/otSDKStub.js`}
                            data-testid="onetrust-sdk-stub-script"
                            data-domain-script={onetrustScriptConfig.dataDomainScript}
                            data-language={onetrustScriptConfig.language}
                            data-document-language={onetrustScriptConfig.documentLanguage ? 'true' : undefined}
                        />
                        <script
                            nonce={nonce}
                            suppressHydrationWarning
                            data-testid="onetrust-optanon-wrapper"
                            // Inline callback OneTrust calls after every consent
                            // decision. Contents are a fixed string literal (no
                            // interpolation of user data), so no XSS surface.
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: optanonWrapperInline }}
                        />
                    </>
                )}
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body className="antialiased flex flex-col min-h-screen">
                {children}
                <AppToaster />
                <ScrollRestoration nonce={nonce} />
                <Scripts nonce={nonce} />
                {/* Dev-only overlay: mounts outside the React tree to avoid interfering with app state/context. Zero production overhead — tree-shaken by Vite when PROD=true. */}
                <UITargetDevModeInit />
            </body>
        </html>
    );
}

function ErrorPageFooter() {
    return (
        <footer className="mt-auto border-t border-footer-foreground/25 bg-footer-background text-footer-foreground">
            <div className="section-container flex flex-col gap-12 py-6 lg:gap-20 lg:py-8">
                <p className="text-center text-xs leading-4 text-footer-foreground">
                    © {new Date().getFullYear()} All rights reserved.
                </p>
            </div>
        </footer>
    );
}

class FooterErrorBoundary extends Component<{ children: ReactNode }> {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        return this.state.hasError ? <ErrorPageFooter /> : this.props.children;
    }
}

function ErrorPageHeader() {
    return (
        <header className="relative bg-header-background text-header-foreground w-full section-container">
            <div className="pt-2 lg:pt-4">
                <div className="flex flex-wrap items-center gap-x-1 lg:gap-x-6">
                    <a href="/" className="flex-1 lg:flex-0 flex items-center">
                        <CompanyLogo title="Ashley" className="h-5" />
                    </a>
                </div>
            </div>
        </header>
    );
}

class HeaderErrorBoundary extends Component<{ children: ReactNode }> {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error: Error) {
        // eslint-disable-next-line no-console
        console.error('[HeaderErrorBoundary] Header render failed, falling back to ErrorPageHeader:', error);
    }
    render() {
        return this.state.hasError ? <ErrorPageHeader /> : this.props.children;
    }
}

/**
 * Error page content component with i18n support.
 * When called without an I18nextProvider (early error before loader ran), t falls back to
 * the key string — callers must pass translated strings directly via props in that case.
 */
function ErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
    title,
    message,
    secondaryMessage,
    statusDetails,
    goToHomepageLabel,
    categories,
    subs,
    newsletterSignupHeading,
    appConfig,
    site,
    locale,
    language,
    currency,
    clientAuth,
    basketSnapshot,
    storeResolution,
    selectedStoreInfo,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
    title: string;
    message?: string;
    secondaryMessage?: string;
    statusDetails?: string;
    goToHomepageLabel: string;
    categories?: Promise<ShopperProducts.schemas['Category']>;
    subs?: Promise<ShopperProducts.schemas['Category'][]>;
    newsletterSignupHeading?: Promise<ContentAssetBody | null>;
    appConfig?: AppConfig;
    site?: Site;
    locale?: Locale;
    language?: string;
    currency?: string;
    clientAuth?: PublicSessionData | null;
    basketSnapshot?: BasketSnapshot | null;
    storeResolution?: {
        resolved: ResolvedStore;
        deliveryZip: DeliveryZip;
        flash: ZipFlashCode | null;
    } | null;
    selectedStoreInfo?: SelectedStoreInfo | null /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
}) {
    const hasFullContext = !!(appConfig && site && locale);

    const headerSection = hasFullContext ? (
        <HeaderErrorBoundary>
            <ResponsiveNavigationMenu resolve={categories} defer={subs}>
                {({ mobileNavigation, desktopNavigation }) => (
                    <Header mobileNavigation={mobileNavigation} desktopNavigation={desktopNavigation} />
                )}
            </ResponsiveNavigationMenu>
        </HeaderErrorBoundary>
    ) : (
        <ErrorPageHeader />
    );

    const footerSection =
        categories && newsletterSignupHeading && hasFullContext ? (
            <FooterErrorBoundary>
                <Footer categories={categories} newsletterSignupHeading={newsletterSignupHeading} />
            </FooterErrorBoundary>
        ) : (
            <ErrorPageFooter />
        );

    const content = (
        <>
            <title>{title}</title>

            {headerSection}

            {/* Main Content */}
            <main className="grow mt-4 mb-12 bg-brand-surface flex flex-col justify-center">
                <div className="section-container w-full">
                    <div className="grid grid-cols-6">
                        <div className="col-span-6 md:col-span-2 flex flex-col justify-center">
                            <h1 className="uppercase tracking-widest">{title}</h1>

                            {message && (
                                <p className="text-4xl md:text-5xl font-publico-headline leading-12 md:leading-14 whitespace-pre-line">
                                    {message}
                                </p>
                            )}
                            {secondaryMessage && (
                                <p className="text-base leading-7 whitespace-pre-line">{secondaryMessage}</p>
                            )}

                            {statusDetails && <p className="text-base leading-7">{statusDetails}</p>}

                            {/* For non-route errors: show technical details */}
                            {details && <p className="text-base leading-7">Error: {details}</p>}

                            {/* Stack trace (only in dev mode with stack) */}
                            {stack && (
                                <div className="hidden border border-border rounded-none bg-muted/30 text-left">
                                    <div className="flex items-center px-4 py-3 border-b border-border">
                                        <h2 className="text-sm font-semibold text-foreground">Stack Trace</h2>
                                    </div>
                                    <pre className="p-4 overflow-auto max-h-40 text-xs leading-relaxed text-foreground/90 font-mono">
                                        <code>{stack}</code>
                                    </pre>
                                    <div className="px-4 py-3 border-t border-border">
                                        <p className="text-xs text-muted-foreground">
                                            To disable stack traces in production, turn off{' '}
                                            <code className="text-xs">unstable_devTools</code> in your router config.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {status === 500 ? (
                                <>
                                    <p className="mt-4 text-base leading-7">
                                        In the meantime, here&apos;s what you can do:
                                    </p>
                                    <ul className="list-disc list-inside text-base leading-7">
                                        <li>Try refreshing the page</li>
                                        <li>Clear browser cache and cookies</li>
                                        <li>Check back with us shortly</li>
                                    </ul>
                                </>
                            ) : (
                                <div className="mt-4">
                                    <Button
                                        size="lg"
                                        className="uppercase text-xs font-bold tracking-button"
                                        onClick={() => {
                                            window.location.href = homepageUrl;
                                        }}>
                                        {goToHomepageLabel}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="col-span-6 md:col-span-4">
                            <ErrorPageImage className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            </main>

            {footerSection}
        </>
    );

    if (hasFullContext) {
        const withStoreDelivery = storeResolution ? (
            <StoreAndDeliveryZipProvider {...storeResolution}>{content}</StoreAndDeliveryZipProvider>
        ) : (
            content
        );

        return (
            <ConfigProvider config={appConfig}>
                <SiteProvider site={site} locale={locale} language={language ?? 'en'} currency={currency ?? ''}>
                    {/* @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */}
                    <StoreLocatorProvider selectedStoreInfo={selectedStoreInfo ?? null}>
                        <AuthProvider value={clientAuth ?? undefined}>
                            <BasketProvider snapshot={basketSnapshot ?? null}>
                                <SearchSuggestionsProvider>{withStoreDelivery}</SearchSuggestionsProvider>
                            </BasketProvider>
                        </AuthProvider>
                    </StoreLocatorProvider>
                </SiteProvider>
            </ConfigProvider>
        );
    }

    return content;
}

/**
 * Renders the error page using translations from the root loader payload.
 * Requires an I18nextProvider wrapping it.
 */
function TranslatedErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
    categories,
    subs,
    newsletterSignupHeading,
    appConfig,
    site,
    locale,
    language,
    currency,
    clientAuth,
    basketSnapshot,
    storeResolution,
    selectedStoreInfo,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
    categories?: Promise<ShopperProducts.schemas['Category']>;
    subs?: Promise<ShopperProducts.schemas['Category'][]>;
    newsletterSignupHeading?: Promise<ContentAssetBody | null>;
    appConfig?: AppConfig;
    site?: Site;
    locale?: Locale;
    language?: string;
    currency?: string;
    clientAuth?: PublicSessionData | null;
    basketSnapshot?: BasketSnapshot | null;
    storeResolution?: {
        resolved: ResolvedStore;
        deliveryZip: DeliveryZip;
        flash: ZipFlashCode | null;
    } | null;
    selectedStoreInfo?: SelectedStoreInfo | null /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
}) {
    const { t } = useTranslation('routeError');

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;
    let statusDetails: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
        statusDetails = t('404.details');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={title}
            message={message}
            secondaryMessage={secondaryMessage}
            statusDetails={statusDetails}
            goToHomepageLabel={t('goToHomepage')}
            categories={categories}
            subs={subs}
            newsletterSignupHeading={newsletterSignupHeading}
            appConfig={appConfig}
            site={site}
            locale={locale}
            language={language}
            currency={currency}
            clientAuth={clientAuth}
            basketSnapshot={basketSnapshot}
            storeResolution={storeResolution}
            selectedStoreInfo={selectedStoreInfo}
        />
    );
}

export function ErrorBoundary({ error }: { error: unknown }) {
    // All hooks must be called unconditionally before any early returns.
    const rootData = useRouteLoaderData<typeof loader>('root');
    const appData = useRouteLoaderData<typeof appLoader>('routes/_app');
    const categories = appData?.root;
    const subs = appData?.subs;
    const newsletterSignupHeading = appData?.newsletterSignupHeading;
    const appConfig = rootData?.appConfig;
    const site = rootData?.site;
    const locale = rootData?.locale;
    const currency = rootData?.currency;
    const clientAuth = rootData?.clientAuth;
    const basketSnapshot = rootData?.basketSnapshot;
    const storeResolution = rootData?.storeResolution;
    const selectedStoreInfo = rootData?.selectedStoreInfo; /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */

    const language =
        (typeof window !== 'undefined' && i18nextOnClient ? i18nextOnClient.language : undefined) ||
        (typeof document !== 'undefined' ? document.documentElement.lang : undefined) ||
        rootData?.locale?.id ||
        'en';

    const errorTranslations = rootData?.errorTranslations;
    const footerTranslations = rootData?.footerTranslations;
    const headerTranslations = rootData?.headerTranslations;
    const accountTranslations = rootData?.accountTranslations;
    const cartTranslations = rootData?.cartTranslations;
    const miniCartTranslations = rootData?.miniCartTranslations;
    const extStoreLocatorTranslations =
        rootData?.extStoreLocatorTranslations; /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */
    const fallbackLng = rootData?.appConfig.i18n.fallbackLng ?? 'en';

    // Only created when errorTranslations is available (root loader ran).
    // When the loader did not run (e.g. middleware crash), errorTranslations is undefined and we
    // skip i18next entirely — rendering hardcoded English strings directly avoids any static import
    // of locale JSON that would cause Vite to bundle/preload locale chunks on every page.
    const i18nextInstance = useMemo(() => {
        if (!errorTranslations || Object.keys(errorTranslations).length === 0) return null;
        const nsBundle = {
            routeError: errorTranslations,
            footer: footerTranslations ?? {},
            header: headerTranslations ?? {},
            account: accountTranslations ?? {},
            cart: cartTranslations ?? {},
            miniCart: miniCartTranslations ?? {},
            extStoreLocator: extStoreLocatorTranslations ?? {} /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
        };
        const resources: Record<string, typeof nsBundle> = {
            [fallbackLng]: nsBundle,
            [language]: nsBundle,
        };
        const instance = createInstance();
        void instance.use(initReactI18next).init({
            lng: language,
            fallbackLng,
            resources,
            interpolation: { escapeValue: false },
            initAsync: false,
        });
        return instance;
    }, [
        language,
        errorTranslations,
        footerTranslations,
        headerTranslations,
        accountTranslations,
        cartTranslations,
        miniCartTranslations,
        extStoreLocatorTranslations,
        fallbackLng,
    ]);

    const homepageUrl = rootData?.site
        ? buildUrl({
              to: '/',
              urlConfig: rootData.appConfig.url,
              params: {
                  siteId: rootData.site.alias ?? rootData.site.id,
                  localeId: rootData.appConfig.localeAliasMap?.[rootData.locale.id] ?? rootData.locale.id,
              },
          })
        : '/';

    // Redirect maintenance errors before rendering.
    if (error && error.toString().includes('MAINTENANCE_ERROR')) {
        return <Navigate to={routes.maintenance} replace />;
    }

    let status: number | undefined;
    let details: string | undefined;
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        status = error.status;
        details = error.statusText;
    } else if (error instanceof Error) {
        status = 500;
        details = error.message;
        stack = error.stack;
    } else if (typeof error === 'string') {
        details = error;
    }

    // Root loader ran — render with active locale translations.
    if (i18nextInstance) {
        return (
            <I18nextProvider i18n={i18nextInstance}>
                <TranslatedErrorPageContent
                    status={status}
                    details={details}
                    stack={stack}
                    homepageUrl={homepageUrl}
                    categories={categories}
                    subs={subs}
                    newsletterSignupHeading={newsletterSignupHeading}
                    appConfig={appConfig}
                    site={site}
                    locale={locale}
                    language={language}
                    currency={currency}
                    clientAuth={clientAuth}
                    basketSnapshot={basketSnapshot}
                    storeResolution={storeResolution}
                    selectedStoreInfo={selectedStoreInfo}
                />
            </I18nextProvider>
        );
    }

    // Root loader did not run (e.g. middleware crash before loader executed).
    // No locale context available — render hardcoded English strings with no i18next dependency.
    const englishContent = {
        404: { title: 'Page not found', message: 'The requested page could not be found.' },
        403: { title: 'Access restricted', message: "You don't have permission to view this page." },
        500: { title: 'Unexpected Error', message: "oops,\nthat's unexpected." },
    };
    const fallbackContent =
        status && status in englishContent
            ? englishContent[status as keyof typeof englishContent]
            : { title: 'Unexpected Error', message: "oops,\nthat's unexpected." };

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={fallbackContent.title}
            message={fallbackContent.message}
            goToHomepageLabel="Return Home"
            categories={categories}
            subs={subs}
            newsletterSignupHeading={newsletterSignupHeading}
            appConfig={appConfig}
            site={site}
            locale={locale}
            language={language}
            currency={currency}
            clientAuth={clientAuth}
            basketSnapshot={basketSnapshot}
            storeResolution={storeResolution}
            selectedStoreInfo={selectedStoreInfo}
        />
    );
}

export default function App({
    loaderData: {
        clientAuth,
        basketSnapshot,
        getI18next,
        currency,
        correlationId,
        pageDesignerMode,
        site,
        locale,
        // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
        selectedStoreInfo,
        // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
        storeResolution,
    },
}: {
    loaderData: LoaderData;
}) {
    // Currency is always provided by loader (which reads from middleware)
    if (!currency) {
        throw new Error('Currency is required but not provided by loader');
    }

    // Get app configuration from server loader data (initial load) or window.__APP_CONFIG__ (client nav)
    // This ensures config is read from MRT environment variables (via middleware), not baked at build time
    const serverData = useRouteLoaderData('root');
    const appConfig = serverData?.appConfig || (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined);
    if (!appConfig) {
        throw new Error('App configuration not available - check server loader and window.__APP_CONFIG__');
    }

    // In server-only auth architecture:
    // - clientAuth contains only non-sensitive fields (userType, customerId, usid, etc.)
    // - These values are serialized directly from the server loader
    // - No client middleware or bootstrap needed - server is the single source of truth
    // - Tokens (accessToken, refreshToken) stay server-side only

    // Initialize Page Designer components
    initializeRegistry();

    const i18next = (typeof window === 'undefined' ? getI18next?.() : i18nextOnClient) as i18n;

    const sites = appConfig.commerce.sites as AppConfig['commerce']['sites'];
    const defaultSite = sites.find((s) => s.id === appConfig.defaultSiteId) ?? sites[0];
    const shopperAgentLocale = i18next?.language ?? defaultSite?.defaultLocale ?? appConfig.i18n.fallbackLng;

    // Memoize the providers array to prevent unnecessary remounting of providers on render
    const providers = useMemo(
        () =>
            [
                [I18nextProvider, { i18n: i18next }],
                [ConfigProvider, { config: appConfig }],
                // Site provider will contain info about site/locale/currency on single request.
                // include i18next.language since these infos tend to go together.
                // site will drive the language/locale and currency
                [SiteProvider, { site, locale, language: i18next.language, currency }],
                [AuthProvider, { value: clientAuth }],
                [BasketProvider, { snapshot: basketSnapshot }],
                [MarketingActivityProvider, {}],
                // INT-43: header search-suggestions adapter (mock | constructor | einstein).
                // The provider lazy-imports whichever adapter is named by
                // `config.searchSuggestions.adapter`; without it, the
                // header `useSearchSuggestionsAdapter()` returns undefined
                // and the popover stays silently empty.
                [SearchSuggestionsProvider, {}],
                [CorrelationProvider, { value: correlationId }],
                // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
                [StoreLocatorProvider, { selectedStoreInfo }],
                // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
                // BROWSE-47: only mount when the store-resolution middleware
                // produced a payload. Null indicates the middleware crashed
                // before context.set() — in that degraded path the consumers
                // (header "Deliver to", PDP delivery box, ZipUpdateToast)
                // simply don't render.
                ...(storeResolution ? ([[StoreAndDeliveryZipProvider, storeResolution]] as const) : ([] as const)),
            ] as const,
        [
            correlationId,
            i18next,
            appConfig,
            currency,
            clientAuth,
            basketSnapshot,
            site,
            locale,
            // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
            selectedStoreInfo,
            // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
            storeResolution,
        ]
    );

    const hybridEnabled = Boolean(appConfig?.hybrid?.enabled);

    return (
        <ComposeProviders providers={providers}>
            <LivePersonProvider config={appConfig.livePerson} deliveryZip={storeResolution?.deliveryZip.zip}>
                <UITargetProviders>
                    <AuthActionExecutor />
                    <ScopeUpgradeLoginPrompt />
                    {hybridEnabled && <BackNavigationRevalidator />}
                    <PageDesignerProvider
                        clientId="storefront-next"
                        targetOrigin="*"
                        usid={clientAuth?.usid}
                        mode={pageDesignerMode}>
                        <PageDesignerInit />
                        <Outlet />
                    </PageDesignerProvider>
                    <TrackingConsentBanner />
                    <OneTrustBridge />
                    {/* BROWSE-47: side-effect-only — fires a one-shot toast when
                        the store-resolution middleware overwrote a shopper's manually
                        entered delivery ZIP because the resolved store is in a different
                        price zone (flow diagram Step C, manual branch). Reads flash from
                        StoreAndDeliveryZipProvider. */}
                    {storeResolution && <ZipUpdateToast />}
                    {typeof window !== 'undefined' && <PageViewTracker />}
                </UITargetProviders>
                {(appConfig.commerceAgent?.enabled === 'true' || appConfig.commerceAgent?.enabled === true) && (
                    <ShopperAgent
                        commerceAgentConfiguration={appConfig.commerceAgent}
                        locale={shopperAgentLocale}
                        currency={currency}
                        userId={clientAuth?.customerId}
                        usid={clientAuth?.usid}
                    />
                )}
            </LivePersonProvider>
        </ComposeProviders>
    );
}

/**
 * Component that executes pending actions after authentication
 * This runs on every route to check if there are actions queued from auth interception
 */
function AuthActionExecutor() {
    useExecutePendingAction();
    return null;
}

/**
 * Revalidates loader data once on back/forward (e.g. back from SFRA). Mounted only when
 * hybrid is enabled; ensures UI is fresh after a full-page redirect. No-op when hybrid is off.
 */
function BackNavigationRevalidator() {
    const revalidator = useRevalidator();
    const didRevalidateRef = useRef(false);
    useEffect(() => {
        if (didRevalidateRef.current) return;
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'back_forward' && revalidator.state === 'idle') {
            didRevalidateRef.current = true;
            void revalidator.revalidate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}

/**
 * Initialize UITarget dev mode overlay (DEV ONLY - zero production overhead)
 * Lazy-loads the overlay when VITE_UI_TARGET_DEV_MODE=true
 */
function UITargetDevModeInit() {
    useEffect(() => {
        // Only runs in browser
        if (typeof window === 'undefined') return;

        // Only in development
        if (import.meta.env.PROD) return;

        // Only if enabled
        if (import.meta.env.VITE_UI_TARGET_DEV_MODE !== 'true') return;

        // Lazy load the overlay
        void import('@/lib/ui-target-dev-mode').then(({ initUITargetDevMode }) => {
            void initUITargetDevMode();
        });
    }, []);

    return null;
}
